export function reverseHeb(s) {
  if (!s) return "";
  return [...s.trim()].reverse().join("").trim();
}

function formatDOB(d8) {
  if (!/^\d{8}$/.test(d8)) return null;
  const dd = d8.slice(0, 2), mm = d8.slice(2, 4), yyyy = d8.slice(4, 8);
  return `${dd}/${mm}/${yyyy}`;
}

// מנרמל מספר פוליסה להשוואה בין מקורות:
// ב-MBT הפוליסה מופיעה כ-"01017580526" ובקובץ האחיד (DAT) כ-"17580526".
// לוקחים את 9 הספרות האחרונות ומסירים אפסים מובילים.
export function normPolNum(polNum) {
  const digits = String(polNum || "").replace(/\D/g, "");
  if (!digits) return "";
  return String(Number(digits.slice(-9)));
}

// ── כותרות פוליסה מ-LIFE.MBT / LIFEHLTH.MBT ─────────────────────────────────
// שתיהן באותו מבנה עמודות (0-based):
//   c8=פוליסה · c9="01"+ת"ז · c14=שם מבוטח (עברית הפוכה) · c15/c16=תחילה/תום
//   c37=פרמיה בסיסית · c48=סה"כ פרמיות ששולמו · c49=סה"כ פרמיה חודשית
//   c74=ערך פדיון (רלוונטי ב-LIFE — פוליסות חיסכון)
function parseHeaderFile(content, kind, headersByPol) {
  content.split("\n").forEach(line => {
    const cols = line.split("|");
    if (cols.length < 50) return;
    const polNum = cols[8]?.trim();
    if (!polNum) return;
    const tazRaw = cols[9]?.trim();
    // c9 = "01" + ת"ז 9 ספרות
    const taz = /^\d{10,}$/.test(tazRaw) ? tazRaw.slice(-9) : null;
    headersByPol.set(polNum, {
      polNum,
      taz,
      insuredName:    reverseHeb(cols[14]) || null,
      startDate:      formatDOB(cols[15]?.trim()),
      endDate:        formatDOB(cols[16]?.trim()),
      basePremium:    parseFloat(cols[37]) || 0,
      premiumsPaid:   parseFloat(cols[48]) || 0,
      monthlyPremium: parseFloat(cols[49]) || 0,
      surrenderValue: parseFloat(cols[74]) || 0,
      kind, // "life" | "health"
    });
  });
}

export function parseMigdalZip(fileName, zipFiles) {
  let companyCode = "10";
  // תאריך נכונות אמיתי משם הקובץ אם קיים; אחרת null — המשתמש בוחר חודש-ערך חובה
  // ב-MigdalImport (אין נפילה ל-new Date() כדי לא לתייג נתוני מאי כיוני בטעות).
  let reportDate = null;
  const m = fileName.match(/(\d{4})(\d{2})(\d{2})/);
  if (m) reportDate = `${m[1]}-${m[2]}-${m[3]}`;

  const personEntry   = zipFiles.find(f => /^PERSON\.MBT$/i.test(f.name));
  const covrlifeEntry = zipFiles.find(f => /^COVRLIFE\.MBT$/i.test(f.name));
  const lifeEntry     = zipFiles.find(f => /^LIFE\.MBT$/i.test(f.name));
  const lifehlthEntry = zipFiles.find(f => /^LIFEHLTH\.MBT$/i.test(f.name));

  // אין MBT — מחזירים ריק; ה-DAT (מבנה אחיד) מטופל בנפרד ב-MigdalImport ויכול לעמוד לבדו.
  if (!personEntry || !covrlifeEntry) {
    return { companyCode, reportDate, fileName, members: [] };
  }

  // Files are UTF-8 encoded
  const enc = new TextDecoder("utf-8");
  const personContent = enc.decode(personEntry.content);
  const covrContent = enc.decode(covrlifeEntry.content);

  // ── 1. חברים (PERSON.MBT) ────────────────────────────────────────────────
  const spMap = new Map();

  personContent.split('\n').forEach(line => {
    if (!line.trim()) return;
    const cols = line.split('|');
    if (cols.length < 25) return;

    const rawId = cols[0].trim();
    const tazRaw = cols[2].trim();
    const taz = /^\d+$/.test(tazRaw) ? String(Number(tazRaw)).padStart(9, '0') : null;

    const email = cols[5]?.trim() || null;
    const city = reverseHeb(cols[16]);
    const lastName = reverseHeb(cols[24]);
    const firstName = reverseHeb(cols[25]);
    const birthDate = formatDOB(cols[26]?.trim());
    const genderRaw = reverseHeb(cols[4]);
    const gender = (genderRaw.includes('נקבה') || genderRaw.includes('ילדה')) ? 'נקבה' : 'זכר';

    if (taz) {
      spMap.set(taz, {
        rawId,
        taz,
        firstName: firstName || null,
        lastName: lastName || null,
        city: city || null,
        email,
        gender,
        birthDate,
        funds: []
      });
    }
  });

  // ── 2. כותרות פוליסה (LIFE + LIFEHLTH) ──────────────────────────────────
  const headersByPol = new Map();
  if (lifeEntry)     parseHeaderFile(enc.decode(lifeEntry.content), "life", headersByPol);
  if (lifehlthEntry) parseHeaderFile(enc.decode(lifehlthEntry.content), "health", headersByPol);

  // ── 3. כיסויים (COVRLIFE) מקובצים לפי פוליסה ─────────────────────────────
  // policyMap: polNum → { taz, coverages: [] }
  const policyMap = new Map();

  covrContent.split('\n').forEach(line => {
    const cols = line.split('|');
    if (cols.length < 20) return;

    const covCode = cols[0]?.trim();
    const polNum = cols[1]?.trim();
    const typeCode = cols[2]?.trim();
    const startDate = formatDOB(cols[4]?.trim());
    const endDate = formatDOB(cols[5]?.trim());

    // c7 = סכום ביטוח / ערך הכיסוי (לכיסויי חיסכון — צבירה; לבריאות — סכום ביטוח או 0)
    const value = parseFloat(cols[7]) || 0;
    const premiumPeriod  = parseFloat(cols[8]) || 0; // פרמיה לתקופת גבייה (למשל רבעונית)
    const premiumMonthly = parseFloat(cols[9]) || 0; // פרמיה חודשית

    const tazRaw = cols[18]?.trim();
    const taz = /^\d+$/.test(tazRaw) ? String(Number(tazRaw)).padStart(9, '0') : null;
    const productName = cols[19]?.trim();

    if (!taz || !productName || !polNum) return;

    if (!policyMap.has(polNum)) policyMap.set(polNum, { taz, coverages: [] });
    policyMap.get(polNum).coverages.push({
      covCode,
      typeCode,
      name: productName,
      startDate,
      endDate,
      value,
      premiumMonthly,
      premiumPeriod,
      statusLabel: /מסולק/.test(productName) ? "מסולק" : "פעיל",
    });
  });

  // ── 4. fund אחד לכל פוליסה ───────────────────────────────────────────────
  for (const [polNum, pol] of policyMap.entries()) {
    const header = headersByPol.get(polNum) || null;
    const taz = pol.taz || header?.taz;
    if (!taz) continue;

    if (!spMap.has(taz)) {
      spMap.set(taz, {
        rawId: null, taz, firstName: null, lastName: null, city: null, gender: null, birthDate: null, funds: []
      });
    }

    // שם הפוליסה — הכיסוי בעל הערך הגבוה ביותר, ואם אין ערכים: הכיסוי הראשון
    const mainCov = [...pol.coverages].sort((a, b) => (b.value || 0) - (a.value || 0))[0];
    const tochnit = mainCov?.name || (header?.kind === "health" ? "פוליסת בריאות" : "פוליסת פרט");

    const sumMonthly = pol.coverages.reduce((s, c) => s + (c.premiumMonthly || 0), 0);
    const allSettled = pol.coverages.every(c => c.statusLabel === "מסולק");

    spMap.get(taz).funds.push({
      id: `10_${polNum}`,
      name: tochnit,
      polNum,
      normPolNum: normPolNum(polNum),
      companyName: "מגדל",
      companyCode: "10",
      // redeem = ערך פדיון מהכותרת בלבד. כיסויי בריאות אינם צבירה —
      // ערכי הכיסויים (סכומי ביטוח) לא נסכמים יותר ל-redeem.
      redeem: header?.surrenderValue || 0,
      premium: header?.monthlyPremium || sumMonthly,
      premiumsPaid: header?.premiumsPaid || 0,
      insuredName: header?.insuredName || null,
      startDate: header?.startDate || mainCov?.startDate || null,
      endDate: header?.endDate || null,
      policyKind: header?.kind || "health",
      statusLabel: allSettled ? "מסולק" : "פעיל",
      kisuimMigdal: pol.coverages,
    });
  }

  // לא זורקים שגיאה על 0 פוליסות — ייתכן שכל הנתונים בקובץ האחיד (DAT);
  // MigdalImport מציג שגיאה רק אם גם ה-DAT וגם ה-MBT ריקים.
  const members = Array.from(spMap.values()).filter(m => m.funds.length > 0);
  return { companyCode, reportDate, fileName, members };
}
